import { Injectable, Injector, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { ToastService } from './toast.service';
import { LoggerService } from './logger.service';
import { RoleService } from './role.service';
import { UserRole } from '../shared/types/role.interface';
import { environment } from '../../environments/environment';
import { LocationService } from './location.service';
import { PackageService } from './package.service';
import { ProfileService } from './profile.service';
import { ModalStateService } from './modal-state.service';
import { EmployeeService } from './employee.service';
import {
  clearActiveImpersonationSessions,
  consumeImpersonationHandoffIntoStorage,
  getActiveUserDataRaw,
  isThisTabImpersonating,
  IMPERSONATION_USER_DATA_KEY,
  removeImpersonationStorageItem,
  setImpersonationStorageItem,
  IMPERSONATION_TUNNEL_DATA_KEY,
} from '../shared/utils/impersonation-handoff.util';
import { ImpersonationService } from '../admin/impersonation/services/impersonation.service';
import { clearCustomThemeColors } from '../shared/utils/theme-color.util';
import { NavigationService } from './navigation.service';
import { FullStoryService } from './fullstory.service';
import { take } from 'rxjs';

/**
 * Authentication and session management service that handles login, logout, token validation, user persistence, role initialization, and force-login flows.
 * @component
 * @selector app-auth-service
 * @example
 *   <app-auth-service [showForceLoginModal]="true" (userChange)="onUserChange($event)"></app-auth-service>
 * @input Signal<boolean> showForceLoginModal - A reactive signal indicating whether the UI should display a force-login confirmation modal (used when concurrent sessions are detected).
 * @output Signal<any> user - Reactive signal that emits the currently authenticated user object or null when unauthenticated; can be observed by components to react to auth state changes.
 *
 * Purpose:
 *   AuthService centralizes authentication logic for the application: it performs login and logout operations, validates tokens with the API, manages persisted user information in localStorage, clears caches on user changes, and coordinates role and profile services.
 *
 * Key properties:
 *   - user: Signal<any> - holds the current authenticated user data (token, id, email, name, role, etc.).
 *   - showForceLoginModal: Signal<boolean> - toggles a modal for forcing a login when conflict (409) occurs.
 *   - pendingCredentials: Signal<any> - stores credentials when a force-login conflict is returned by the API.
 *
 * Important methods:
 *   - validateToken(): Observable<any>
 *       Validates the current session token with the backend. On success it updates stored user email and keeps the session. On failure it silently clears cached auth data and optionally shows an error toast unless already on the login page.
 *
 *   - login(credentials: { email: string, password: string, rememberMe: boolean }, force: boolean = false): Observable<any>
 *       Attempts to authenticate with the API. On success it delegates to handleLoginSuccess to persist and apply user state. On a 409 conflict it saves pending credentials and opens the force-login modal.
 *
 *   - forceLogin(): Observable<any>
 *       Retries a previously pending login with force_logout=true to resolve concurrent session conflicts. Returns null if no pending credentials exist.
 *
 *   - cancelForceLogin(): void
 *       Clears any pending credentials and hides the force-login modal.
 *
 *   - logout(): void
 *       Performs a logout sequence: hides modal state, attempts a non-blocking API logout, clears all cached location/package/user data and related role/profile state, and navigates to the login route. API errors are handled silently and logged.
 *
 *   - setUser(userData: any): void
 *       Sets the user signal and persists the userData to localStorage (if running in a browser).
 *
 *   - forgotPassword(email: string): Observable<any>
 *       Sends a forgot-password request to the backend.
 *
 *   - resetPassword(password: string, token: string): Observable<any>
 *       Sends a reset-password request to the backend with token and new password.
 *
 *   - handleLoginSuccess(data: any, rememberMe: boolean): void (private)
 *       Internal routine executed after a successful login: clears caches, composes a normalized user object, persists saved users when rememberMe is set, updates role service, navigates to the role-specific default route, and clears pending credentials.
 *
 *   - clearUserData(): void (private)
 *       Clears location/package caches, removes persisted userData, resets the user signal, clears role and profile state, and routes to login if not already there. Used to silently clear state on token invalidation or switch scenarios.
 *
 *   - loadUserFromStorage(): void (private)
 *       Called from the constructor to initialize the user signal from localStorage. Respects route contexts (admin/tunnel) to avoid mixing sessions and ensures role service is set accordingly.
 *
 * Lifecycle and initialization:
 *   - constructor(apiService, router, http, messageService, logger)
 *       Injects necessary services and immediately calls loadUserFromStorage() to bootstrap user state from localStorage and initialize role state. No Angular lifecycle hooks (ngOnInit, etc.) are used because this is an injectable service; initialization happens in the constructor.
 *
 * Behavior notes:
 *   - Token validation and logout handle errors silently in many scenarios to avoid spurious toasts when switching between user types.
 *   - The service cooperates with RoleService, LocationService, PackageService, ProfileService and ModalStateService to keep application state consistent after auth events.
 *   - Saved user entries are stored under 'saved_user' in localStorage to support "remember me" and quick account switching.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user = signal<any>(null);
  public showForceLoginModal = signal(false);
  private pendingCredentials = signal<any>(null);
  /** Bumps on logout/clear so in-flight shop-profile fetches cannot stamp stale FullStory vars. */
  private fullStorySessionGeneration = 0;

  private apiUrl = `${environment.baseUrl}/${environment.apiVersion}`;
  private roleService = inject(RoleService);
  private locationService = inject(LocationService);
  private packageService = inject(PackageService);
  private profileService = inject(ProfileService);
  private modalStateService = inject(ModalStateService);
  private employeeService = inject(EmployeeService);
  /** Lazy resolve ImpersonationService to avoid circular DI with AuthService. */
  private readonly injector = inject(Injector);
  private navigationService = inject(NavigationService);
  private fullStoryService = inject(FullStoryService);

  constructor(
    private apiService: ApiService,
    private router: Router,
    private http: HttpClient,
    private messageService: ToastService,
    private logger: LoggerService
  ) {
    this.loadUserFromStorage();
  }

  /**
   * Validate the current authentication token with the backend and update or clear local user data.
   * @example
   * validateToken()
   * Observable<any> emitting the server validation response (or error if validation fails)
   * @param {void} none - No arguments.
   * @returns {Observable<any>} Observable that emits the validation response or errors if validation fails.
   **/
  validateToken(): Observable<any> {
    return this.apiService.get('user/auth/session').pipe(
      tap((response: any) => {
        if (response.status === true && response.user) {
          const localData = getActiveUserDataRaw();
          if (localData) {
            try {
              const userData = JSON.parse(localData);
              userData.email = response.user.email;
              this.setUser(userData);
              // FullStory enrichment only after session is confirmed — never from storage restore.
              this.identifyFullStorySession(userData);
            } catch {
              this.clearUserData();
              // Let catchError toast — storage was corrupt before a session toast ran.
              throw new Error('Stored session is invalid');
            }
          } else {
            const current = this.user();
            if (current) {
              this.identifyFullStorySession({
                ...current,
                email: response.user.email || current.email,
              });
            }
          }
        } else {
          // Clear + toast here; mark handled so catchError does not double-notify.
          this.clearUserData(true);
          const message = response.reason || 'Your session has expired. Please login again.';
          if (!this.router.url.includes('/login')) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: message,
              life: 3000,
            });
          }
          throw Object.assign(new Error(response.message || 'Token is invalid'), {
            authHandled: true,
          });
        }
      }),
      catchError(error => {
        // Validation failures already cleared state (and toasted) in tap — rethrow only.
        if (error?.authHandled) {
          return throwError(() => error);
        }
        // Transport / unexpected failures: clear without wiping theme palette.
        this.clearUserData();
        const reason = error?.error?.reason || 'Your session has expired. Please login again.';
        if (!this.router.url.includes('/login')) {
          this.messageService.add({
            severity: 'error',
            summary: "You've been logged out",
            detail: reason,
            life: 3000,
          });
        }
        return throwError(
          () => new Error(error?.error?.message || error?.message || 'Token validation failed')
        );
      })
    );
  }

  /**
   * Clears cached user-related data (location cache, packages cache, localStorage) and resets user, role, and profile state, then navigates to the login page without calling the logout API.
   * @example
   * clearUserData()
   * void
   * @param clearThemePalette When true, also removes browser-local custom theme colors (confirmed session expiry only).
   * @returns {void} Does not return a value.
   **/
  private clearUserData(clearThemePalette = false): void {
    // Clear cached data and local storage without calling logout API
    // This prevents error toasts when switching between user types
    this.fullStorySessionGeneration += 1;
    this.fullStoryService.clearSession();
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    clearActiveImpersonationSessions();
    localStorage.removeItem('userData');
    if (clearThemePalette) {
      clearCustomThemeColors();
    }
    this.user.set(null);
    // Clear role and profile state
    this.roleService.clearUserRole();
    this.profileService.clearUserProfile();
    if (!this.router.url.includes('/login')) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Attach FullStory user identity (email + shop) for session search.
   * Call only after login success or a successful validateToken() — never from loadUserFromStorage,
   * so company/me cannot race AuthGuard and trigger a 401 soft-logout before /auth/session.
   * Safe no-op when FS is absent (skips the authenticated shop-profile fetch too).
   */
  private identifyFullStorySession(
    userData: { id?: string | number; email?: string; name?: string } | null
  ): void {
    if (!userData?.id && !userData?.email) {
      return;
    }

    const uid = String(userData.id || userData.email || '').trim();
    if (!uid) {
      return;
    }

    const sessionGeneration = this.fullStorySessionGeneration;
    this.fullStoryService.identifyUser({
      uid,
      email: userData.email,
      displayName: userData.name,
    });

    // Shop name is FullStory-only enrichment; skip authenticated fetch when the snippet is absent.
    if (!this.fullStoryService.getFsApi()?.identify) {
      return;
    }

    this.profileService
      .getBusinessProfile()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (sessionGeneration !== this.fullStorySessionGeneration) {
            return;
          }
          const current = this.user();
          const currentUid = String(current?.id || current?.email || '').trim();
          if (!current || currentUid !== uid) {
            return;
          }
          const shopName =
            response?.status && response?.data?.name ? String(response.data.name).trim() : '';
          if (shopName) {
            this.fullStoryService.setShopName(shopName);
          }
        },
        error: () => {
          // Shop name is enrichment only — never block auth on profile fetch failures.
        },
      });
  }

  private isImpersonatingSession(): boolean {
    // Tab-scoped only — another tab's impersonation must not redirect this logout.
    return isThisTabImpersonating() || !!this.user()?.impersonated;
  }

  /**
   * Perform application logout: hide any open modal, call logout API (errors handled silently), clear caches and local storage, reset user/role/profile state, and navigate to the login page.
   * While impersonating, logout exits impersonation (back to Way Superuser) instead of a normal shop login redirect.
   * @example
   * ogout()
   * undefined
   * @returns {void} Does not return a value; triggers logout side effects and navigation to the login route.
   **/
  logout(): void {
    // Close notification modal immediately on logout
    this.modalStateService.hideModal();

    // Attempt to call logout API, but don't block on errors
    // This prevents error toasts when switching between user types
    this.apiService.logout().subscribe({
      next: () => {
        // Logout successful
      },
      error: err => {
        // Silently handle logout errors (e.g., token already invalid, network issues)
        // This is expected when switching between user types
        this.logger.debug(
          'Logout API call failed (expected in some scenarios)',
          err,
          'AuthService'
        );
      },
    });

    // Impersonation logout = exit impersonation (keep Way Superuser session intact).
    if (this.isImpersonatingSession()) {
      this.logger.info('Logout during impersonation — exiting impersonation', {}, 'AuthService');
      this.fullStorySessionGeneration += 1;
      this.fullStoryService.clearSession();
      // Lazy get avoids circular constructor DI (ImpersonationService -> AuthService).
      this.injector.get(ImpersonationService).exitImpersonation();
      return;
    }

    // Always clear cached data and local storage regardless of API call result
    this.fullStorySessionGeneration += 1;
    this.fullStoryService.clearSession();
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    clearActiveImpersonationSessions();
    localStorage.removeItem('userData');
    localStorage.removeItem('tunnelData');
    clearCustomThemeColors();
    this.user.set(null);
    // Clear role and profile state
    this.roleService.clearUserRole();
    this.profileService.clearUserProfile();
    this.router.navigate(['/login']);
  }

  setUser(userData: any): void {
    this.user.set(userData);
    if (typeof window !== 'undefined') {
      if (userData?.impersonated) {
        setImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY, JSON.stringify(userData));
        removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
      } else {
        // Prevent stale tab-scoped impersonation from overriding the new login.
        removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
        removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
        localStorage.setItem('userData', JSON.stringify(userData));
      }
    }
    this.navigationService.restoreSidebarCollapsedForCurrentUser();
  }

  /**
   * Attempts to log in a user with provided credentials and handles force logout flow.
   * @example
   * ogin({ email: 'user@example.com', password: 'pass123', rememberMe: true }, true)
   * Observable<any>
   * @param {{ { email: string, password: string, rememberMe: boolean } }} {{credentials}} - User credentials containing email, password and rememberMe flag.
   * @param {{boolean}} {{force}} - If true, forces logout of existing sessions; defaults to false.
   * @returns {{Observable<any>}} Observable that emits the login response data, or null when a force-login confirmation is required.
   **/
  login(
    credentials: { email: string; password: string; rememberMe: boolean },
    force = false
  ): Observable<any> {
    const payload = {
      // svc-user matches on `WHERE email = $1` (userModel.findByEmail), so a stray
      // space from autofill or a paste makes a valid account fail with a flat
      // "Invalid Email or Password" — the same 401 an unknown email returns.
      // Trim here; case-insensitive matching still has to be fixed server-side.
      email: credentials.email?.trim(),
      password: credentials.password,
      force_logout: force,
    };
    return this.apiService.postWithoutAuth('user/auth/login', payload).pipe(
      tap((response: any) => {
        if (response.status === true && response.data) {
          this.handleLoginSuccess(response.data, credentials.rememberMe);
        } else {
          throw new Error(response.message || 'Login failed');
        }
      }),
      catchError(error => {
        if (error.status === 409 || (error.error && error.error.status_code === 409)) {
          this.pendingCredentials.set(credentials);
          this.showForceLoginModal.set(true);
          return of(null);
        }
        return throwError(() => error.error || new Error('An any error occurred'));
      })
    );
  }

  forceLogin(): Observable<any> {
    const credentials = this.pendingCredentials();
    if (credentials) {
      this.showForceLoginModal.set(false);
      return this.login(credentials, true);
    }
    return of(null);
  }

  cancelForceLogin(): void {
    this.showForceLoginModal.set(false);
    this.pendingCredentials.set(null);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/auth/password/forgot`, { email });
  }

  resetPassword(password: string, token: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/auth/password/reset`, { password, token });
  }

  /**
   * Handle successful login: clear caches, set current user and optionally persist user to localStorage, set role and navigate to default route.
   * @example
   * handleLoginSuccess({ token: 'abc123', user: { id: 1, email: 'user@example.com', name: 'User', picture_url: null, role: 'admin', location_id: 42 } }, true)
   * undefined
   * @param {{any}} {{data}} - Login response payload containing token and user information.
   * @param {{boolean}} {{rememberMe}} - Flag indicating whether to persist the user in localStorage.
   * @returns {{void}} Void return; updates application state and performs navigation.
   **/
  private handleLoginSuccess(data: any, rememberMe: boolean): void {
    // Clear cached data on login to fetch fresh data for the new user
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    this.profileService.clearBusinessProfileCache();

    const userData = {
      token: data.token,
      id: data.user.id, // Include user ID
      email: data.user.email,
      name: data.user.name,
      profileImage: data.user.picture_url || 'assets/images/switch-account/Frame%201171276019.svg',
      is_remember: rememberMe,
      date: new Date(),
      location: data.user.location_id,
      timezone: typeof data.user.timezone === 'string' ? data.user.timezone.trim() : undefined,
      role: data.user.role || 'admin', // Include role in user data
      lane_id: data.user.selected_lane_id ?? data.user.lane_id ?? null,
    };

    const savedUsersRaw = localStorage.getItem('saved_user');
    let savedUsers: any[] = [];
    if (savedUsersRaw) {
      try {
        let parsedData = JSON.parse(savedUsersRaw);
        if (!Array.isArray(parsedData)) {
          parsedData = [parsedData];
        }
        savedUsers = parsedData;
      } catch (error) {
        this.logger.error('Error parsing saved users from localStorage', error, 'AuthService');
        savedUsers = [];
      }
    }

    const userIndex = savedUsers.findIndex(user => user.email === userData.email);
    const isAlreadySaved = userIndex > -1;

    if (rememberMe || isAlreadySaved) {
      userData.is_remember = true;
      this.setUser(userData);

      if (isAlreadySaved) {
        savedUsers[userIndex] = userData;
      } else {
        savedUsers.push(userData);
      }
      localStorage.setItem('saved_user', JSON.stringify(savedUsers));
    } else {
      this.setUser(userData);
    }

    // Set user role in role service
    const userRole = (data.user.role as UserRole) || UserRole.ADMIN;
    this.roleService.setUserRole(userRole);
    this.identifyFullStorySession(userData);

    // Navigate to appropriate dashboard based on role
    const defaultRoute = this.roleService.getDefaultRoute(userRole);
    void this.router.navigateByUrl(defaultRoute);
    this.pendingCredentials.set(null);
  }
  /**
   * Load user data from local storage into the service, handling admin and tunnel routes specially.
   * @example
   * loadUserFromStorage()
   * undefined
   * @returns {void} Initializes in-memory user state and user role based on stored data; no value returned.
   **/
  private loadUserFromStorage(): void {
    // Check if we're on an admin or tunnel route - if so, use separate storage
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;

      // New-tab impersonation: install handoff session before normal auth bootstrap.
      if (!currentPath.startsWith('/admin/')) {
        consumeImpersonationHandoffIntoStorage();
      }

      if (currentPath.startsWith('/admin/')) {
        // On admin routes, check superAdminData first
        const superAdminData = localStorage.getItem('superAdminData');
        if (superAdminData) {
          try {
            const parsedData = JSON.parse(superAdminData);
            if (parsedData?.token && parsedData?.role === UserRole.SUPER_ADMIN) {
              // Don't set user signal for super admin (keep it separate)
              // Role will be set by the guard
              return;
            }
          } catch (error) {
            // Invalid superAdminData, continue to check userData
          }
        }
        // If no valid superAdminData on admin route, don't load regular userData
        // Let the guard handle redirecting to login
        return;
      }

      if (currentPath.startsWith('/tunnel')) {
        // On tunnel routes, tunnel data is handled by TunnelGuard
        // Don't load regular userData to avoid mixing sessions
        return;
      }
    }

    // Prefer impersonation session (new-tab handoff) over normal shop userData.
    const localData = getActiveUserDataRaw();
    if (localData) {
      const userData = JSON.parse(localData);
      this.user.set(userData);

      if (userData.role) {
        const userRole = userData.role as UserRole;
        this.roleService.setUserRole(userRole);
      } else {
        this.roleService.setUserRole(UserRole.ADMIN);
      }
      // Do not call identifyFullStorySession here — that fires company/me before
      // AuthGuard validateToken and can clear the session via the 401 interceptor.
    }
  }
}
